/**
 * Post Show Render
 *
 * DOM rendering helpers for post detail images, tiles, and show-page UI fragments.
 */

import { phpUrl } from "../../core/php-data-access";
import commonUtil from "../../util/common-utils";
import { UISymbols } from "../../constants/ui-symbols";
import { Roles } from "../../constants/roles";
import { Placeholders } from "../../constants/placeholders";
import { ImageZoomSetup } from "../../components/controls/image-zoom/image-zoom-setup";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { postShowForm } from "./post-show-form";

interface RenderAttachedImagesOptions {
    attachedImages: string[];
    boardId: string;
    userId: number | null | undefined;
    removedImages?: string[];
}

interface RemoveAttachedImagesOptions {
    removedImages: string[];
    boardId: string;
    userId: number | null | undefined;
}

interface RenderExistingEditImageTilesParams {
    imageManager: HTMLElement;
    attachedFilenames: string[];
    boardId: string | null | undefined;
    userId: number | null | undefined;
    insertBeforeNode?: Element | null;
}

interface RenderExistingEditVideoTilesParams {
    grid: HTMLElement;
    attachedFilenames: string[];
    boardId: string | null | undefined;
}

function renderAttachedImages(postContainer: HTMLElement, options: RenderAttachedImagesOptions): void {
    const { attachedImages, boardId, userId, removedImages: _removedImages = [] } = options;
    void _removedImages;

    if (!postContainer || !boardId) return;

    const attached = Array.isArray(attachedImages)
        ? attachedImages.filter((value) => typeof value === "string" && value.trim() !== "")
        : [];

    const attachmentsSection = postShowForm.getPostAttachmentsSection(postContainer);
    if (!(attachmentsSection instanceof HTMLElement)) {
        console.warn("[PostShowRender] No attachments section found in postContainer");
        return;
    }

    let mediaBlock = postShowForm.getPostAttachmentBlock(attachmentsSection);

    if (attached.length === 0) {
        if (mediaBlock) {
            const attachmentContent = postShowForm.getPostAttachmentContent(mediaBlock);
            if (attachmentContent) {
                postShowForm.getPostAttachmentImageItems(attachmentContent).forEach((item) => item.remove());
                const hasVideos = postShowForm.getPostAttachmentVideoItem(attachmentContent);
                if (!hasVideos) {
                    mediaBlock.style.display = "none";
                }
            }
        }
        return;
    }

    if (!mediaBlock) {
        mediaBlock = createAttachmentBlock("attached_medias", "Attached Medias");
        attachmentsSection.appendChild(mediaBlock);
    }

    mediaBlock.style.display = "";

    const attachmentContent = postShowForm.getPostAttachmentContent(mediaBlock);
    if (!(attachmentContent instanceof HTMLElement)) {
        console.warn("[PostShowRender] No attachment content found in mediaBlock");
        return;
    }

    postShowForm.getPostAttachmentImageItems(attachmentContent).forEach((item) => item.remove());

    const firstVideoItem = postShowForm.getPostAttachmentVideoItem(attachmentContent);

    attached.forEach((filename) => {
        const item = document.createElement("div");
        item.className = "post-image-item post-image-item--zoom";
        item.setAttribute("data-role", Roles.POST_ATTACHMENT_IMAGE_ITEM);
        item.setAttribute("data-image-zoom-root", "");
        item.setAttribute("data-image-zoom-popup-x", "right");
        item.setAttribute("data-image-zoom-popup-y", "bottom");

        const source = document.createElement("div");
        source.className = "post-image-source";
        source.setAttribute("data-image-zoom-source", "");

        const img = document.createElement("img");
        img.className = "post-image";
        img.setAttribute("data-role", Roles.POST_ATTACHMENT_IMAGE);
        img.alt = "Post Image";
        const url = commonUtil.resolvePostImageUrl(boardId, userId, filename);
        if (url) {
            img.src = url;
        }

        source.appendChild(img);
        item.appendChild(source);

        const panel = document.createElement("div");
        panel.className = "post-image-zoom-panel";
        panel.setAttribute("data-image-zoom-panel", "");
        panel.setAttribute("aria-hidden", "true");

        const zoomTarget = document.createElement("img");
        zoomTarget.className = "post-image-zoom-panel-image";
        zoomTarget.alt = "Post Image";
        zoomTarget.setAttribute("data-image-zoom-target", "");
        if (url) {
            zoomTarget.src = url;
        }

        panel.appendChild(zoomTarget);
        item.appendChild(panel);

        if (firstVideoItem) {
            attachmentContent.insertBefore(item, firstVideoItem);
        } else {
            attachmentContent.appendChild(item);
        }

        ImageZoomSetup.init(item);
    });
}

function removeAttachedImages(postContainer: HTMLElement, options: RemoveAttachedImagesOptions): void {
    const { removedImages, boardId, userId } = options;
    if (!postContainer || !boardId || !Array.isArray(removedImages) || removedImages.length === 0) {
        return;
    }

    const items = postShowForm.getPostAttachmentImages(postContainer);
    items.forEach((img) => {
        const src = img.getAttribute("src") || "";
        if (
            removedImages.some(
                (name) =>
                    name &&
                    ((phpUrl.base.postImages()
                        ? src.includes(`${phpUrl.base.postImages()}/${boardId}/${userId}/${name}`)
                        : false) ||
                        src.endsWith(`/${name}`)),
            )
        ) {
            postShowForm.getClosestPostAttachmentImageItem(img)?.remove();
        }
    });
}

function createAttachmentBlock(translateKey: string, defaultText: string): HTMLElement {
    const block = document.createElement("div");
    block.className = "attachment-label";
    block.setAttribute("data-role", Roles.POST_ATTACHMENT_BLOCK);

    const header = document.createElement("div");
    header.className = "attachment-header";

    const label = document.createElement("span");
    label.className = "attachment-label-text";
    label.setAttribute("data-translate", translateKey);
    label.textContent = defaultText;

    header.appendChild(label);

    const content = document.createElement("div");
    content.className = "attachment-content";
    content.setAttribute("data-role", Roles.POST_ATTACHMENT_CONTENT);

    block.appendChild(header);
    block.appendChild(content);

    return block;
}

function renderHiddenSnsInputs(editForm: HTMLFormElement, links: string[]): void {
    if (!(editForm instanceof HTMLFormElement)) return;
    if (!Array.isArray(links) || links.length === 0) return;

    const html = links
        .map((link) => {
            const safeValue = commonUtil.escapeHtmlAttribute(String(link || ""));
            return `<input type="hidden" name="sns_links[]" value="${safeValue}" data-role="${Roles.SNS_LINK_INPUT}">`;
        })
        .join("");

    if (html) {
        editForm.insertAdjacentHTML("beforeend", html);
    }
}

function renderExistingEditImageTiles(params: RenderExistingEditImageTilesParams): void {
    const { imageManager, attachedFilenames, boardId, userId, insertBeforeNode = null } = params;
    if (!(imageManager instanceof HTMLElement)) return;
    if (!Array.isArray(attachedFilenames) || attachedFilenames.length === 0) return;

    const html = attachedFilenames
        .map((filename, index) => {
            const url = commonUtil.resolvePostImageUrl(boardId, userId, filename) ?? "";
            const safeSrc = commonUtil.escapeHtmlAttribute(String(url || ""));
            const safeFilename = commonUtil.escapeHtmlAttribute(String(filename || ""));
            const safeIndex = String(index);

            return `
                <div class="picker-tile picker-tile-with-controls existing-image-tile" data-role="${Roles.MEDIA_TILE}" data-item-role="${Roles.EXISTING_IMAGE_TILE}" data-image-type="existing" data-image-id="${safeIndex}" data-image-index="${safeIndex}" data-image-filename="${safeFilename}">
                    <img class="existing-image" alt="Existing Image ${index + 1}" src="${safeSrc}" data-image-id="${safeIndex}">
                    <div class="picker-controls" data-role="${Roles.MEDIA_CONTROLS}">
                        <button type="button" class="picker-rotate" data-role="${Roles.MEDIA_ROTATE}" title="หมุนรูปภาพ" aria-label="หมุนรูปภาพ">↻</button>
                        <button type="button" class="btn circle-clear media-picker picker-remove btn-remove-existing" data-role="${Roles.MEDIA_REMOVE}" title="ลบรูปภาพ" aria-label="ลบรูปภาพ"></button>
                    </div>
                    <div class="removed-indicator" data-role="${Roles.MEDIA_REMOVED_INDICATOR}" style="display:none;"><i class="bi bi-trash"></i> ลบแล้ว</div>
                </div>
            `;
        })
        .join("");

    if (!html) return;

    if (insertBeforeNode instanceof Element) {
        insertBeforeNode.insertAdjacentHTML("beforebegin", html);
        return;
    }

    const pickerBody = postShowForm.getPickerBody(imageManager);
    if (pickerBody instanceof HTMLElement) {
        pickerBody.insertAdjacentHTML("beforeend", html);
    }
}

function renderExistingEditVideoTiles(params: RenderExistingEditVideoTilesParams): void {
    const { grid, attachedFilenames, boardId } = params;
    if (!(grid instanceof HTMLElement)) return;
    if (!Array.isArray(attachedFilenames) || attachedFilenames.length === 0) return;

    const html = attachedFilenames
        .map((filename, index) => {
            const safeFilename = commonUtil.escapeHtmlAttribute(String(filename || ""));
            const safeIndex = String(index);
            const videoSrc = boardId
                ? commonUtil.escapeHtmlAttribute(`/videos/posts/${String(boardId)}/${String(filename)}`)
                : "";

            return `
                <div class="picker-tile picker-tile-with-controls existing-video-tile" data-role="${Roles.MEDIA_TILE}" data-item-role="${Roles.EXISTING_VIDEO_TILE}" data-video-type="existing" data-video-index="${safeIndex}" data-video-filename="${safeFilename}">
                    <video class="existing-video" controls preload="metadata" playsinline aria-label="Existing Video ${index + 1}">
                        <source src="${videoSrc}" type="video/mp4">
                    </video>
                    <div class="picker-controls" data-role="${Roles.MEDIA_CONTROLS}">
                        <button type="button" class="btn circle-clear media-picker picker-remove btn-remove-existing" data-role="${Roles.MEDIA_REMOVE}" title="ลบวิดีโอ" aria-label="ลบวิดีโอ"></button>
                    </div>
                    <div class="removed-indicator" data-role="${Roles.MEDIA_REMOVED_INDICATOR}" style="display:none;"><i class="bi bi-trash"></i> ลบแล้ว</div>
                </div>
            `;
        })
        .join("");

    if (html) {
        grid.insertAdjacentHTML("beforeend", html);
    }
}

function renderLinkedProductPreviewShell(node: HTMLElement, postProductIdInt: number): HTMLElement | null {
    if (!(node instanceof HTMLElement)) return null;
    if (!Number.isInteger(postProductIdInt) || postProductIdInt <= 0) return null;

    node.setAttribute("data-role", Roles.POST_PRODUCT_LINKER_VIEW);
    node.classList.add("has-product");
    node.style.display = "";
    delete node.dataset.adApplied;
    node.dataset.productLinkerUnresolved = "0";
    node.innerHTML = `
        <input type="hidden" value="${commonUtil.escapeHtmlAttribute(String(postProductIdInt))}" data-role="${Roles.POST_PRODUCT_ID}">
        <div class="post-product-linker__preview" data-role="${Roles.POST_PRODUCT_PREVIEW}"></div>
    `;

    return postShowForm.getPostProductPreview(node);
}

function renderLinkedProductEmptyState(node: HTMLElement): void {
    if (!(node instanceof HTMLElement)) return;

    node.setAttribute("data-role", Roles.POST_PRODUCT_LINKER_VIEW);
    node.classList.remove("has-product");
    delete node.dataset.adApplied;
    node.dataset.productLinkerUnresolved = "1";
    const hideEmpty = node.dataset.hideEmpty === "1";
    const showEmpty = node.dataset.showEmpty === "1";
    const isInsidePostCard = postShowForm.getClosestPostCard(node) instanceof HTMLElement;

    if (hideEmpty) {
        node.style.display = "none";
        node.innerHTML = "";
        return;
    }

    node.style.display = "";

    if (isInsidePostCard) {
        node.innerHTML = "";
        return;
    }

    if (!showEmpty) {
        node.innerHTML = "";
        return;
    }

    const starFilled = UISymbols.STAR_FILLED;

    node.innerHTML = `
        <div class="post-product-linker__card" aria-disabled="true">
            <div class="post-product-linker__card-link">
                <div class="post-product-linker__thumb" data-role="${Roles.POST_PRODUCT_THUMB}">
                    <div class="board-placeholder post-product-linker__thumb-placeholder" data-role="${Roles.POST_PRODUCT_THUMB_PLACEHOLDER}" data-placeholder-variant="no-image">
                        <div class="placeholder-icon text-icon"><i class="bi bi-image"></i></div>
                        <div class="placeholder-text final-placeholder">${Placeholders.NO_IMAGE}</div>
                    </div>
                </div>
                <div class="post-product-linker__meta">
                    <div class="post-product-linker__name text-muted">---</div>
                    <div class="post-product-linker__brand text-muted">&nbsp;</div>
                    <div class="post-product-linker__stats" aria-hidden="true">
                        <span class="post-product-linker__stat post-product-linker__stat--rating">
                            <span class="post-product-linker__star-char" aria-hidden="true">${starFilled}</span>
                            <span class="rating-average">--</span>
                        </span>
                        <span class="post-product-linker__stat post-product-linker__stat--reviews">
                            <i class="bi bi-chat-left-text" aria-hidden="true"></i>
                            <span class="product-review-count">--</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function renderSnsLinksData(contentElement: HTMLElement, snsLinks: string[]): void {
    if (!(contentElement instanceof HTMLElement)) return;

    const existing = postShowForm.getSnsLinksData(contentElement);
    if (existing) {
        existing.remove();
    }

    if (!Array.isArray(snsLinks) || snsLinks.length === 0) {
        return;
    }

    const cleanedLinks = SNSLinksUtils.normalizeSnsLinks(snsLinks);
    if (cleanedLinks.length === 0) {
        return;
    }

    const safeLinks = commonUtil.escapeHtmlAttribute(JSON.stringify(cleanedLinks));
    contentElement.insertAdjacentHTML(
        "beforeend",
        `<div class="sns-links-data" data-role="${Roles.POST_SNS_LINKS_DATA}" style="display:none;" data-sns-links="${safeLinks}"></div>`,
    );
}

export {
    removeAttachedImages,
    renderAttachedImages,
    renderExistingEditImageTiles,
    renderExistingEditVideoTiles,
    renderHiddenSnsInputs,
    renderLinkedProductEmptyState,
    renderLinkedProductPreviewShell,
    renderSnsLinksData,
};
